import './EsNavBar.css';

import EsNavLogo from './EsNavLogo';
import EsFullscreenToggler from "../../common/EsFullscreenToggler";
import EsThemeMenu from "../../common/EsThemeMenu";
import EsLocaleMenu from "../../common/EsLocaleMenu";
import EsAccountCircle from "../../common/EsAccountCircle";
import EsCcuCount, { useEsCcuCount } from "../../common/EsCcu";
import EsNotificationBell from "../../common/EsNotificationBell";
import '../../js/themeNav';
import {useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore} from "react";
import {doNavInit} from "../../js/themeNav";
import {
    ADMIN_PACK_REVIEW,
    API_LOGOUT,
    VIEW,
    VIEW_ADMIN,
    VIEW_CAMPAIGNS,
    VIEW_CHARACTERS,
    VIEW_CODEX,
    VIEW_CHAT,
    VIEW_CONSOLE,
    VIEW_DEMO,
    VIEW_ACCOUNT,
    VIEW_LOGIN,
    VIEW_REGISTER,
    VIEW_SUBSCRIBE,
    VIEW_THEME,
    VIEW_TRANSLATE
} from "../../common/EsConstants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {useAuth} from "./EsAuth";
import { useEsCharactersFlag, useEsTranslationsFlag, useEsCrafterFlag, useEsCodexFlag, useEsChatFlag, useEsDashboardFlag, useEsDemoModeFlag, useEsDualNavScoped, useEsCcuPlace } from "../../common/EsFlags";
import { esCrafterComponentPath, esCrafterHas, esCrafterList, useEsCrafterLocalized } from "../../common/EsCrafter";
import { useEsLocale } from "../../common/EsLocale";
import { useEsStrings } from "../../common/EsStrings";
import { FREE_REASONS, useEsSubMe } from "../../common/EsSubs";
import { useEsLastGame } from "../game/EsLastGame";
import {serverUrl} from "../../common/EsEnv";
import { esSubmitLogout } from "../../common/EsFetch";

// PHONE WIDTH IS BELOW sm: Tailwind's 40rem (see EsCommon.css @theme), the break the row's
// own phone rules (max-sm:text-sm) and the wordmark cap in EsNavLogo
// already key off. A literal because a media query cannot read a theme
// variable; the two must move together. jsdom has no matchMedia and is wide.
const SM_QUERY = '(min-width: 640px)';

const subscribeSm = ( pNotify ) => {
    if ( !window.matchMedia ) return () => {};
    const aQuery = window.matchMedia( SM_QUERY );
    aQuery.addEventListener( 'change', pNotify );
    return () => aQuery.removeEventListener( 'change', pNotify );
};

const isPhone = () => Boolean( window.matchMedia ) && !window.matchMedia( SM_QUERY ).matches;

// A row entry trades .navItem's list margins, which are mx-8 below lg, for a
// disc's gap.
const ROW_ENTRY = "ms-0 me-1 whitespace-nowrap max-sm:text-sm sm:me-3";

export default function EsNavBar() {
    const t = useEsStrings();
    const auth = useAuth();
    // The reader sees pText, in their locale; the key, the id, and so the testid stay the
    // English word's, so es-nav-codex-link and #Features hold in every language.
    const item = ( pWord, pText, pRest ) => ( { ...pRest, key: pWord.toLowerCase().replace( / +/g, "-" ), id: pWord, text: pText } );
    const codexOn = useEsCodexFlag();
    const charactersOn = useEsCharactersFlag();
    const chatOn = useEsChatFlag();
    // The value alone. useEsDashboardFlag returns { ready, on } because EsDash
    // has to tell "not answered yet" from "off" -- a nav bar does not: an entry
    // that appears a beat late is what every other flagged entry here already
    // does, and rendering it during the wait would be a link to a gate that
    // redirects.
    const { on: dashboardOn } = useEsDashboardFlag();
    const demoOn = useEsDemoModeFlag();
    // DEMO, THE SHARED TABLE (T46), behind switchEsDemoMode as the table itself is: last on the
    // marketing links, and in Session's place on the signed-in row while there is no Session,
    // since a reader with a game running has a table of their own to go back to. Signed out it
    // leads through Login, as VIEW_DEMO is behind RequireAuth.
    const demoItem = item( "Demo", t( 'nav-menu-demo', 'Demo' ), { view: VIEW_DEMO } );

    // switchEsCrafter on: the labels and the marketing links are the site-nav component's, in
    // the reader's locale. Each entry carries a KEY, today's English word, and the testid
    // is built from it, so a relabelled or French entry keeps es-nav-<key>-link. Which
    // entries show is still decided here, by the same flags. Off, crafterNav is null and
    // every line below is the bar as it was. A language Crafter has no folder for reads the
    // copykeys, not Crafter's English (esCrafterHas).
    const locale = useEsLocale();
    const crafterOn = useEsCrafterFlag() && esCrafterHas( locale );
    const { item: navItem } = useEsCrafterLocalized( ( pLocale ) => esCrafterComponentPath( pLocale, 'site-nav' ), locale, crafterOn );
    const crafterNav = navItem?.root ?? null;
    const crafterLabels = {};
    esCrafterList( crafterNav?.labels_o ).forEach( ( pLabel ) => { crafterLabels[ pLabel.key_s ] = pLabel.label_s; } );
    const crafterEntry = ( pItem ) => {
        if ( !crafterNav ) return pItem;
        const aKey = pItem.key ?? pItem.text.toLowerCase().replace( / +/g, "-" );
        return { ...pItem, key: aKey, text: crafterLabels[ aKey ] ?? pItem.text, id: pItem.id ?? pItem.text };
    };

    // SUBSCRIBE LEADS THE RIGHT-HAND GROUP, and says Donate only to an account
    // that is free for life -- admin, alpha, and beta, reason LIFETIME, the same
    // test EsAccountSub makes for its Donate (FREE_REASONS). Paid, trial, and lapsed all
    // read Subscribe (Tim, 2026-10-11). Both words go to the Subscribe page and
    // share es-nav-subscribe-link.
    // Nothing until subs/me answers, so the word never flips in front of
    // somebody who has paid. Signed out, nothing at all: Register and Login
    // stand there instead, on the same auth.authenticated test that picks
    // publicRightItems (Tim, 2026-10-19).
    const { me: subMe } = useEsSubMe( auth.authenticated );
    const subscribeWord = auth.loading || !auth.authenticated ? null
        : !subMe ? null
        : FREE_REASONS.includes( subMe.reason ) ? "Donate"
        : "Subscribe";
    // A fixed key, not the text, so both words answer es-nav-subscribe-link.
    // Subscribe takes the site-nav component's label when switchEsCrafter is on;
    // that component has no Donate.
    const subscribeItem = subscribeWord && {
        ...crafterEntry( item( "Subscribe", t( 'nav-menu-subscribe', 'Subscribe' ), { view: VIEW_SUBSCRIBE } ) ),
        key: "subscribe",
        ...( subscribeWord === "Donate" ? { text: t( 'nav-menu-donate', 'Donate' ), id: "Donate" } : {} ),
    };

    let homeItems = crafterNav
        ? esCrafterList( crafterNav.marketing_o )
            .map( ( pLink ) => ( { key: pLink.key_s, text: pLink.label_s, href: pLink.href_s,
                                   id: pLink.key_s.charAt( 0 ).toUpperCase() + pLink.key_s.slice( 1 ) } ) )
        : [
        item( "Features", t( 'nav-menu-features', 'Features' ), { href: "/#features" } ),
        item( "Principles", t( 'nav-menu-principles', 'Principles' ), { href: "/#principles" } ),
        item( "Roadmap", t( 'nav-menu-roadmap', 'Roadmap' ), { href: "/#roadmap" } ),
        item( "Finances", t( 'nav-menu-finances', 'Finances' ), { href: "/#finances" } ),
        item( "Contact", t( 'nav-menu-contact', 'Contact' ), { href: "/#contact" } ),
    ];
    if ( demoOn ) homeItems = [ ...homeItems, demoItem ];

    // CHANGELOG AND DEVLOG MOVED TO THE FOOTER, 2026-09-15.
    //
    // They were the first two entries here, and they are the only two that are
    // neither a marketing anchor nor a way in -- a reader who wants the release
    // history or the build diary is not the reader the top of the page is
    // written for. The footer already carries that kind of link: Legal &
    // Credits, the copyright and the running version all live there, and these
    // two read as the same register.
    //
    // What this leaves is a right-hand list that is only the login pair, and
    // that is the whole point -- it now swaps cleanly for the account circle
    // when a session arrives, with nothing in it that has to survive the swap.
    // Subscribe is not in it; signed in, it leads the control row (see
    // subscribeItem).
    //
    // ONE ACTION ON A PHONE (Tim, 2026-10-13): below sm the bar carries the one
    // most relevant to the reader and the hamburger keeps the rest. Signed in
    // that is already Subscribe or Donate in the control row. Signed out it is
    // Login, which moves out of this list into the row -- moves, not copies, so
    // es-nav-login-link stays one element (see the note above es-nav-right).
    // Not while /authUser is out, so a signed-in phone never sees Login flash.
    const phone = useSyncExternalStore( subscribeSm, isPhone, () => false );
    const loginItem = item( "Login", t( 'nav-menu-login', 'Login' ), { view: VIEW_LOGIN } );
    const loginInRow = phone && !auth.authenticated && !auth.loading;
    const publicRightItems = [
        item( "Register", t( 'nav-menu-register', 'Register' ), { view: VIEW_REGISTER } ),
        ...( loginInRow ? [] : [ loginItem ] )
    ];

    // Admin for an admin, and the flag it reads has been sitting there unused.
    // EsAuth already derives auth.admin from the Cognito groups the /authUser
    // answer carries; this entry was unconditional, so every logged-in reader
    // was offered a screen that then answered its own fetch 403.
    //
    // Cosmetic, and deliberately only that. esback is the gate and stays the
    // gate: EsAuthConfig names API_ACCOUNT_LIST and API_HISTORY for ROLE_ADMIN
    // and covers every POST/PUT/PATCH/DELETE under /api/, so the 14 admin
    // routes keep RequireAuth and a pasted /view/admin URL still loads and
    // still gets refused. admin/Accounts.feature asserts that refusal by its
    // number and is unchanged -- what this removes is the INVITATION, not a
    // protection, and a menu entry that leads only to an error is a bug of a
    // different kind.
    // Translate for an account with a locale group to translate in (/authUser's translator),
    // behind rolloutEsTranslations as /translate is. Its testid is es-nav-translate-link.
    const copykeysOn = useEsTranslationsFlag();
    const translates = copykeysOn && ( auth.account?.translator ?? [] ).length > 0;
    const authRightItems = [
        ...( auth.admin
            ? [ item( "Admin", t( 'nav-menu-admin', 'Admin' ), { view: VIEW_ADMIN } ) ]
            : [] ),
        // An alpha reviews the Library too (Tim, 2026-10-31) but has no Admin menu, so Review is
        // theirs here; an admin's is the Admin menu's tile. esback's EsPackService.mayReview gates it.
        ...( codexOn && !auth.admin && auth.account?.role === 'ALPHA'
            ? [ item( "Review", t( 'nav-menu-review', 'Review' ), { view: ADMIN_PACK_REVIEW } ) ]
            : [] ),
        ...( translates ? [ item( "Translate", t( 'nav-menu-translate', 'Translate' ), { view: VIEW_TRANSLATE } ) ] : [] ),
        // Your own account. Unconditional for a logged-in reader and behind no
        // flag: everybody who can log in has a nickname and a password, so
        // there is no state in which this entry leads somewhere that refuses
        // the person who clicked it -- which is the test the Admin entry above
        // failed until it learned to read auth.admin.
        //
        // Its data-testid derives from the text, like every other entry in
        // these three arrays: es-nav-account-link. See navTestId below. The
        // screen's own ids are es-account-*; this one belongs to the nav bar
        // and takes the nav bar's prefix.
        // Subscribe left this menu for the bar itself on 2026-10-11, first in
        // the control row; see subscribeItem.
        item( "Account", t( 'nav-menu-account', 'Account' ), { view: VIEW_ACCOUNT } ),
        // Ungated like Account: the Colorsheet route is public (index.js), so
        // nobody who can see this entry is refused by the page. Its testid is
        // es-nav-colorsheets-link.
        item( "Colorsheets", t( 'nav-menu-colorsheets', 'Colorsheets' ), { view: VIEW_THEME } ),
        // LOGOUT IS A FORM POST, and the href beside it is not a leftover.
        //
        // esback turned CSRF on, and Spring's LogoutFilter then matches POST
        // only -- so following this href logs nobody out; `submit` is what
        // ends the session. See esSubmitLogout in EsFetch.js for why a form
        // and not a fetch.
        //
        // The href stays because it is where this entry honestly points and
        // because the alternative does not pass review: jsx-a11y's
        // anchor-is-valid rejects href="#" with a handler on it -- the exact
        // complaint recorded against the old <a onClick={navigate}> entries
        // further down this file. A real href keeps the element focusable and
        // hoverable, and keeps navTestId deriving es-nav-logout-link from the
        // text like every other entry.
        item( "Logout", t( 'nav-menu-logout', 'Logout' ), { href: serverUrl + API_LOGOUT, submit: esSubmitLogout } )
    ];

    // Codex only while rolloutEsCodex is on. A menu entry pointing at a route
    // that redirects straight back to the home page is worse than no entry, and
    // this is the "hide the menu entry" half of the flag -- the other half is
    // EsCodexGate on the routes themselves, because hiding a link does not stop
    // anyone typing the URL. See EsFlags.js.
    // Characters sits beside the Codex on the same terms and behind its own
    // flag, which defaults OFF -- so this entry is absent on a checkout with no
    // LaunchDarkly client ID and on any environment whose dashboard has no
    // rolloutEsCharacters flag in it. That is the intended state until the
    // feature ships; see EsFlags.js.
    // Read from useLocation rather than window.location: see "Which of Codex / Characters /
    // Talks the reader is inside" below.
    const aPath = useLocation().pathname;
    const navigate = useNavigate();
    const { game: lastGame, close: closeSession } = useEsLastGame();
    const scopedNav = useEsDualNavScoped();
    const inLastGame = Boolean( lastGame ) && ( aPath === VIEW_CONSOLE + "/" + lastGame.key
        || aPath.startsWith( VIEW_CONSOLE + "/" + lastGame.key + "/" ) );
    const dashItems = [
        // `match` is the prefix that means "inside this section", which is not
        // always the link's own target. Every codex route happens to begin
        // /view/codex, so VIEW_CODEX covers codexToc, codexSearch, codexSection
        // and the rest. The characters routes do NOT nest that way -- they are
        // deliberately siblings (/view/characters, /view/characterSheet,
        // /view/characterNew; see the note in EsConstants about why the sheet
        // is not /view/characters/:id) -- so the section is the prefix they
        // share rather than the list page's own path.
        ...( codexOn ? [ item( "Codex", t( 'nav-menu-codex', 'Codex' ), { view: VIEW_CODEX } ) ] : [] ),
        // The Commons, the out-of-game chat, right of the Codex (Tim, 2026-09-28; renamed from Chat
        // 2026-10-22), behind rolloutEsChat. "Chat" stays the id: it makes es-nav-chat-link and the
        // site-nav component's key.
        ...( chatOn ? [ item( "Chat", t( 'nav-menu-chat', 'Commons' ), { view: VIEW_CHAT } ) ] : [] ),
        ...( charactersOn
            ? [ item( "Characters", t( 'nav-menu-characters', 'Characters' ), { view: VIEW_CHARACTERS, match: VIEW + "character" } ) ]
            : [] ),
        // Campaigns, behind rolloutEsConsole, and the same sibling problem
        // one word over: /view/campaigns is the list and /view/campaign/:key is
        // one of them, so the entry has to match the section rather than its
        // own target or the highlight goes out the moment you open a campaign.
        //
        // This is the only Campaigns entry there is. A second one -- Dash --
        // sat in authRightItems until 2026-09-16, unconditional, pointing at
        // /view/dash, which EsDash renders by delegating to this very page. Two
        // entries, one screen, and Tim removed the duplicate.
        //
        // What went with it, deliberately and with the consequence named: Dash
        // was the ONLY entry on the bar for a logged-in reader that did not
        // require already being on a /view/ url, so it was the way from the
        // home page into this group. There is now no such way. A reader who
        // logs in lands on "/" with Admin, Account and Logout, and reaches the
        // Codex, Characters, Campaigns or Talks by a url rather than by the
        // bar. /view/console still resolves for anybody holding the link.
        ...( dashboardOn
            ? [ item( "Campaigns", t( 'nav-menu-campaigns', 'Campaigns' ), { view: VIEW_CAMPAIGNS, match: VIEW + "campaign" } ) ]
            : [] ),
        // SESSION, RIGHT OF CAMPAIGNS (Tim, 2026-10-22): while this account has a session
        // running at a table where it plays a character or is the GM -- esback's
        // campaignMemory/session, the most recently opened such table, and the game page's
        // own word while it is open. An observer has none. It is
        // the selected entry on that game's page, and pressing it elsewhere goes back to the
        // game as it was left: the screens, split, sheet, and codex are remembered per account
        // per campaign (EsGameMemory), and the drafts kept for the page load (useEsKept). It
        // replaced the Console entry that led this list outside a game (2026-10-20).
        // variantEsDualNav scoped: the table's name is its tip, since this row names the
        // site and not the table (plans/nav-consolidation-plan-2026-10-20.md, "Outer and
        // inner nav"). `current` rather than `match`: a prefix would light it on a longer key.
        //
        // ITS × CLOSES THE ENTRY, NOT THE SESSION (Tim, 2026-10-22: "just close the nav
        // item"). A button beside the link rather than inside it, so the word still opens the
        // game. esback keeps it closed for that session on every device and falls through to
        // the next table (EsCampaignMemoryService.closeSession); opening the game again, or a
        // new session there, brings it back. Closed from the game's own page, the reader goes
        // to Campaigns, since the entry that marked where they were is gone.
        ...( lastGame
            ? [ item( "Session", t( 'nav-menu-session', 'Session' ), {
                view: VIEW_CONSOLE + "/" + lastGame.key,
                current: inLastGame,
                tip: scopedNav ? lastGame.name : undefined,
                close: {
                    label: t( 'nav-menu-session-close', 'Close {name} session', { name: lastGame.name } ),
                    onClose: () => {
                        closeMenu();
                        if ( inLastGame ) void navigate( VIEW_CAMPAIGNS );
                        void closeSession();
                    },
                } } ) ]
            : demoOn ? [ demoItem ] : [] ),
        // RULES LEFT THE BAR, 2026-10-20: the rulesystems are the codex rail's Rulesys
        // tab (EsCodexRulesysList), at /view/rules beside the Packs page
        // (plans/nav-consolidation-plan-2026-10-20.md, es-todo T60).
        // TALKS MOVED TO THE FOOTER, 2026-09-17, beside the Changelog and the
        // Devlog -- the same trip those two made on 2026-09-15 and for the same
        // reason. See EsFooter, which also names what the move costs: Talks is
        // behind RequireAuth and the footer is shown logged out.
    ];

    // Looked at deliberately when the codex went public on 2026-09-01, rather
    // than discovered later (plan §5 flags it by name).
    //
    // leftNav keys off auth.authenticated, so a logged-out reader on a codex
    // page gets homeItems -- the marketing anchors -- and not the Codex/Talks
    // pair a logged-in reader gets. That is left exactly as it was, and it is
    // the right answer for two reasons. dashItems links to Talks, which is
    // still behind RequireAuth and would be a link straight to the login page.
    // And in-codex navigation is not this bar's job at all: EsCodexNav puts
    // Search, Contents and Tags on every codex page, and the sidebar tree is
    // there beside them, so a logged-out reader is not short of a way around
    // the codex -- only of a way back out to the marketing site, which is what
    // homeItems is.
    //
    // What IS missing is the other direction: nothing on the marketing nav
    // links INTO the codex, so a visitor who has never logged in has no way to
    // discover that the public codex exists. That belongs to the home page and
    // the footer rather than here, and is called out in the handover.
    //
    // ⚠️ THE PATH TEST IS GONE, 2026-09-17, at Tim's word: "when you're logged
    // in, the home nav should be replaced with the logged nav, even on the home
    // page." It used to read
    //
    //     auth.authenticated && window.location.href.includes( VIEW )
    //
    // so a logged-in reader who navigated to / -- the logo links there, and it
    // is where a login used to land people -- got the marketing anchors back:
    // Features, Principles, Roadmap, Finances, Contact, and no way to reach the
    // codex, their characters or the console without using the browser's back
    // button. The bar told them they were logged out while the right-hand side
    // of the same bar offered them Logout.
    //
    // Being logged in is now the whole of the question, which is what the
    // comment above always claimed it was ("leftNav keys off
    // auth.authenticated") and what it stopped being when the path test was
    // added beside it.
    //
    // The second reason to be glad it went: window.location is not state.
    // React does not re-render on it, so the answer was whatever the URL
    // happened to be when the bar last redrew for some other reason -- a
    // client-side route change from /view/codex to / left the old list in
    // place, and the bug was intermittent in exactly the way that makes people
    // doubt they saw it. aPath reads useLocation for precisely this
    // reason and now has no counter-example above it.
    //
    // EMPTY MEANS THE MARKETING LINKS, 2026-10-11 (Tim: "it should show if the
    // other nav isn't showing"). Every entry in dashItems is behind a rollout
    // flag, so a member whose flags were all off was signed in to a bar with
    // nothing on its left. Signed in AND given somewhere to go is the question.
    const dashNav = dashItems.map( crafterEntry );
    //
    // UNKNOWN IS NOT LOGGED OUT (Tim, 2026-10-22). Until /authUser has answered,
    // auth.authenticated is false only because nothing has asked yet, and the bar
    // drew the marketing anchors and Login/Register for that beat on every full
    // load. While auth.loading the lists are empty: a neutral bar, not a guess.
    let leftNav = auth.loading ? [] : auth.authenticated && dashNav.length ? dashNav : homeItems;

    // Which of Codex / Characters / Talks the reader is inside.
    //
    // startsWith on the item's own view rather than an equality test, because
    // every one of these sections is a family of routes: /view/codex is the
    // entry, /view/codex/goblin an entry within it, /view/characterSheet/7 a
    // character. An equality test would light the tab up on the section's front
    // page and go dark the moment anyone went anywhere.
    //
    // Read from useLocation rather than window.location. This has to re-render
    // when the route changes, and window.location is not state -- the highlight
    // would stay wherever it was when the bar last happened to redraw. leftNav
    // above used to have the same flaw and no longer does; see the note there.
    //
    // homeItems are anchors on the marketing page and match nothing here, so a
    // logged-out visitor simply gets no highlight, which is correct.
    // aPath itself is read with dashItems, above, which Session needs.

    // THIS ROW IS THE SITE'S, IN A GAME TOO (Tim, 2026-10-20): it keeps the four lists on
    // it at every width that fits, and the hamburger is the measured fold's alone. A fold in
    // a game was tried and hid the lists. Both variantEsDualNav designs share this; scoped
    // adds the table's name at the head of the screens bar under it (EsGameScreens)
    // (plans/nav-consolidation-plan-2026-10-20.md, "Outer and inner nav").

    // A stable hook per entry for the cucumber suite, derived from the entry's
    // own text rather than its position: es-nav-codex-link, es-nav-campaigns-link,
    // es-nav-logout-link. The list is built from three arrays that change with
    // the reader's auth state and with two feature flags, so a positional
    // selector would address a different link on every one of those paths.
    const navTestId = ( pItem ) => "es-nav-"
        + ( pItem.key ?? pItem.text.toLowerCase().replace( / +/g, "-" ) ) + "-link";

    const isCurrent = ( pItem ) => {
        if ( pItem.current !== undefined ) return pItem.current;
        const aPrefix = pItem.match ?? pItem.view;

        return Boolean( aPrefix ) && aPath.startsWith( aPrefix );
    };
    // subscribeItem is already Crafter's, and re-deriving its key from the text
    // would make Donate es-nav-donate-link.
    let rightNav = ( auth.loading ? [] : auth.authenticated ? authRightItems : publicRightItems )
        .map( ( pItem ) => pItem === subscribeItem ? pItem : crafterEntry( pItem ) );

    // ==== The mobile panel is OPEN or CLOSED in React state, 2026-09-15.
    //
    // It used to be a class on #navbarCollapse that js/themeNav.js toggled from
    // a listener it added to #navbarToggler in doNavInit(). That is the same
    // shape the darkToggler block was in before it moved to EsThemeContext, and
    // themeNav.js's own note about that move is the argument: two things with an
    // opinion about one class is where a control works until it does not. React
    // owns this element's className and renders it from `menuOpen`, so there is
    // one place the panel opens and this is it.
    //
    // Three defects go with the old shape, and they are worth naming because
    // each of them is a way for the menu to be dead on a phone:
    //
    //   - doNavInit bound the CLOSE-on-tap handler to whichever <a> elements
    //     existed at mount -- document.querySelectorAll( '#navbarCollapse ul li
    //     ... a' ) is a snapshot. leftNav changes the moment /authUser answers
    //     and again when a flag lands, and React replaces those <li> nodes, so
    //     after a login the panel no longer closed when anything in it was
    //     tapped. onClick on the entry itself cannot go stale.
    //   - a throw anywhere earlier in doNavInit left the toggler unwired and
    //     the hamburger inert, with nothing on screen to say so. The toggler no
    //     longer depends on that function running at all; see the try below.
    //   - the listener was added with no cleanup, so a second call to doNavInit
    //     would have bound a second listener and the two toggles would have
    //     cancelled: one tap, no change. StrictMode being commented out in
    //     index.js is the only reason that never bit.
    const [ menuOpen, setMenuOpen ] = useState( false );

    const closeMenu = () => setMenuOpen( false );

    // A navigation closes the panel. Tapping an entry closes it directly
    // (onClick below) and that covers the ordinary case including the home-page
    // hash anchors, which change no route at all; this covers the rest -- a
    // redirect out of RequireAuth or a flag gate, and the back button.
    useEffect( () => {
        setMenuOpen( false );
    }, [ aPath ] );

    // doNavInit still wires the sticky header and the back-to-top button, and
    // it reaches for elements this component does not own. It is no longer
    // allowed to take the nav menu with it if one of them is missing: the menu
    // is React's now, and a legacy helper throwing is a console error rather
    // than a bar nobody can open.
    useEffect( () => {
        try {
            doNavInit();
        } catch ( aError ) {
            console.error( 'doNavInit failed; the nav menu is unaffected', aError );
        }
    }, [] );

    // THE BAR'S OWN HEIGHT, PUBLISHED AS --es-nav-h.
    //
    // The five home entries above are plain <a href="/#features"> and the rest,
    // so a click is a native fragment jump: the browser puts the section's top
    // edge at the top of the viewport, which -- once this bar is `fixed` -- is
    // underneath this bar. The section heading landed behind it. The fix is
    // scroll-margin-top on the sections (EsHome.css, and the ribbon's share in
    // EsRibbon.css); this effect supplies the number. The bar is the same height
    // loose and stuck, and the logo's heights are fixed, so it no longer grows as
    // the logo decodes late; it is still measured rather than typed because the
    // items and the round controls are sized in rem and follow a reader's root
    // font.
    //
    // FEATURE-TESTED, not environment-tested. jsdom has no ResizeObserver, and
    // `new ResizeObserver(...)` there is a TypeError rather than a no-op, which
    // would redden every jest test that mounts this bar. Same shape as the
    // scrollIntoView guard in EsScrollIntoView.js. With no observer the property
    // is never written, the rules fall back to 0px, and the page is exactly what
    // it was.
    //
    // A LAYOUT effect, so the number is on <html> before any ordinary effect
    // runs. EsScrollToTop's is one of those, and on a load of /#features it
    // reads the section's scroll-margin-top, which is built from this
    // property; read before it was written, the margin came out 61px short
    // and the heading landed under the bar.
    const aBarRef = useRef( null );

    useLayoutEffect( () => {
        const aBar = aBarRef.current;
        if ( !aBar || typeof ResizeObserver === 'undefined' ) return;

        const aRoot = document.documentElement;
        const publish = () => aRoot.style.setProperty( '--es-nav-h', aBar.offsetHeight + 'px' );

        const aObserver = new ResizeObserver( publish );
        aObserver.observe( aBar );
        publish();

        return () => {
            aObserver.disconnect();
            aRoot.style.removeProperty( '--es-nav-h' );
        };
    }, [] );

    // THE LINKS FOLD INTO THE HAMBURGER WHEN THEY DO NOT FIT, measured, not by
    // breakpoint. A label's length is its locale's: French "Feuille de route" at ~1900px
    // wrapped and doubled the bar's height (Tim, 2026-10-19). .navItem never
    // wraps, so a row too narrow overflows instead; check() below says how.
    //
    // No flicker loop: the overflowing width is kept once measured, and the bar
    // opens again only when the row is at least that wide. Folding never changes
    // the row's own width, so folding cannot unfold it. A change of locale,
    // entries, or face (fontsTick) forgets the number and re-measures, in layout
    // effects, so the open state is never painted on the way. Feature-tested on
    // ResizeObserver like --es-nav-h above; jsdom never folds.
    const aRowRef = useRef( null );
    const aNeedRef = useRef( 0 );
    const [ squeezed, setSqueezed ] = useState( false );
    const [ fontsTick, setFontsTick ] = useState( 0 );
    // The live count on the row (EsCcu.js) arrives after the bar is drawn and grows with the
    // crowd, so its width in digits is part of the key: a count that no longer fits folds the bar.
    const ccuPlace = useEsCcuPlace();
    const ccuOnRow = auth.authenticated && ccuPlace === "nav";
    const ccuCount = useEsCcuCount( ccuOnRow );
    const ccuWidth = ccuOnRow && ccuCount !== null ? ccuCount.toLocaleString().length : 0;
    // The bell is a fixed disc whose badge rides over it, so only whether it is drawn moves the row.
    const bellOnRow = Boolean( auth.authenticated && chatOn );
    const fitKey = [ locale, fontsTick, subscribeItem?.text, loginInRow, ccuWidth, bellOnRow,
        ...leftNav.map( ( pItem ) => pItem.text ), "|", ...rightNav.map( ( pItem ) => pItem.text ) ].join( "\u0001" );

    useEffect( () => {
        let aLive = true;
        document.fonts?.ready?.then( () => aLive && setFontsTick( 1 ) );
        return () => { aLive = false; };
    }, [] );

    useLayoutEffect( () => {
        aNeedRef.current = 0;
        setSqueezed( false );
    }, [ fitKey ] );

    useLayoutEffect( () => {
        const aRow = aRowRef.current;
        if ( !aRow || typeof ResizeObserver === 'undefined' ) return;

        // The row's two in-flow boxes, the logo and the rest, laid end to end.
        // Not scrollWidth: that counts the theme and language menus' closed,
        // absolutely placed panels, which overflow the row at any width. A flex
        // item stops shrinking at its min-content, and nowrap labels make that
        // the links' full length, so the sum is the width the open bar needs.
        // One pixel of slack for offsetWidth's rounding.
        const check = () => {
            const aSum = Array.from( aRow.children ).reduce( ( pSum, pChild ) => pSum + pChild.offsetWidth, 0 );
            if ( aNeedRef.current ) {
                if ( aRow.clientWidth + 1 >= aNeedRef.current ) {
                    aNeedRef.current = 0;
                    setSqueezed( false );
                }
            } else if ( aSum > aRow.clientWidth + 1 ) {
                aNeedRef.current = aSum;
                setSqueezed( true );
            }
        };

        const aObserver = new ResizeObserver( check );
        aObserver.observe( aRow );
        aRow.querySelectorAll( 'nav, ul' ).forEach( ( pEl ) => aObserver.observe( pEl ) );
        check();

        return () => aObserver.disconnect();
    }, [ fitKey, squeezed ] );

    const folded = squeezed;

    // A desktop-only class, dropped while the links are folded so the bar
    // wears its below-lg shape at any width.
    const wide = ( pClasses ) => folded ? "" : " " + pClasses;

    // What an <a> entry does when it is clicked. An entry carrying `submit` does
    // that INSTEAD of following its href -- only Logout does -- and every entry
    // closes the mobile panel either way.
    //
    // Composed here rather than written into the Logout entry itself, because
    // an entry that brought its own handler would have to remember closeMenu as
    // well, and a second copy of that is the divergence the note below is about.
    const navClick = ( pItem ) => ( pEvent ) => {
        closeMenu();

        if ( pItem.submit ) {
            pEvent.preventDefault();
            pItem.submit();
        }
    };

    // One renderer for an entry, used by both lists. It was written out twice --
    // once for leftNav and once for rightNav -- and the two copies have to agree
    // about the testid, the current-page marking AND now about closing the
    // panel, which is three chances to update one of them and not the other.
    const navLink = ( pItem, pClass = "" ) => pItem.view
        ? <Link id={pItem.id ?? pItem.text} data-testid={ navTestId( pItem ) } to={ pItem.view } onClick={ closeMenu } data-tip={ pItem.tip } aria-current={ isCurrent( pItem ) ? 'page' : undefined } className={ "menu-scroll navItem" + ( isCurrent( pItem ) ? " navItemOn" : "" ) + ( pClass ? " " + pClass : "" ) }>  { pItem.text } </Link>
        : <a id={pItem.id ?? pItem.text} data-testid={ navTestId( pItem ) } href={ pItem.href } onClick={ navClick( pItem ) } className="menu-scroll navItem"> { pItem.text } </a>;

    // An entry with `close` (only Session) carries its own × beside the link: its own hit
    // target and tab stop, so the link keeps the click on its word. me-0 hands the link's
    // trailing margin to the ×, which sits against the word in the row and in the panel.
    const navList = ( pItems ) => pItems.map( pItem =>
        <li key={pItem.key ?? pItem.text} className={ "relative group" + ( pItem.close ? " flex items-center" : "" ) }>
            { pItem.close ? navLink( pItem, "me-0" ) : navLink( pItem ) }
            { pItem.close &&
                <button type="button" data-testid={ "es-nav-" + pItem.key + "-close" }
                        aria-label={ pItem.close.label } title={ pItem.close.label } onClick={ pItem.close.onClose }
                        className="ms-1.5 inline-flex size-6 items-center justify-center rounded-full border border-faint text-chrome-ink ring-focus hover:bg-panel-hover focus:outline-none focus-visible:ring-2">
                    <i className="pi pi-times text-xs" aria-hidden="true" />
                </button> }
        </li>
    );

    return (
        <header ref={ aBarRef } data-testid="es-nav-bar" className={ "absolute top-0 inset-s-0 z-40 flex w-full items-center border-b border-section-rule bg-chrome navBar" + ( folded ? " navSqueezed" : "" ) }>
            { /* Full width, not .container: the container's max-width centered the row, so on a
                 wide screen the logo sat far in from the edge the screen bar below starts at. The
                 logo hugs the left, or the hamburger when it shows (Tim, 2026-10-22). */ }
            <div className="w-full px-4">
                <div ref={ aRowRef } className="relative flex items-center justify-between mx-[-16px]">
                    { /* Content-sized: the logo is images at fixed heights (EsNavLogo),
                         so its width is known and nothing here sets it. shrink-0 at
                         every width because the box's flex sibling asks for w-full;
                         from lg the nav's lists share that row, and below it the
                         discs do, and the logo must never be what gives (Tim,
                         2026-10-12). The bar has to genuinely fit as well -- the
                         budget is in EsNavBar.css beside .navItem. */ }
                    { /* ps-7: the mug's left edge on the screen bar's first tab label below, the
                         bar's 16px gutter plus a tab's 12px padding (Tim, 2026-10-22). The row's
                         far end mirrors it: pe-7 on the box below, so the account disc sits as far
                         from the right edge as the mug from the left. From sm only: below it the
                         row keeps ps-4 and pe-2, since the 28px insets put a signed-in phone's row
                         past the window and the page scrolled sideways. Below sm the box is the one
                         thing on the row that gives, and only its wordmark shrinks (EsNavLogo):
                         the label beside it is its locale's length, which no fixed cap can know
                         (locale sweep, 2026-10-19). ps-2 on the box beside it keeps that label off
                         the wordmark, which it touched in de, es, fr, and pl. */ }
                    <div className="flex min-w-0 shrink items-center pe-1 ps-4 sm:shrink-0 sm:pe-4 sm:ps-7">
                        { /* THE HAMBURGER LEADS THE BAR, left of the logo, whenever it shows
                             (Tim, 2026-10-22; 2026-10-20 had put it right of the logo). It
                             shares the logo's box rather than standing as a third box in the
                             row, because the fold's check() and its jest stand-in read the row
                             as two boxes: the logo and the rest. Open, lg:hidden makes it 0px,
                             so the fold measures what it always did; folded, unfolding reads
                             only the row's own width. Its panel (#navbarCollapse, below) is
                             positioned against the .relative row, so it drops from the bar's
                             bottom edge at the row's start, under this button. -ms-2 and
                             me-2 trade the box's left inset for air before the mug, so the
                             bar is no wider than with the button right of the logo. */ }
                        <button
                            id="navbarToggler"
                            data-testid="es-nav-toggler"
                            aria-label={ t( 'nav-bar-mobile-menu', 'Mobile Menu' ) }
                            aria-expanded={ menuOpen }
                            aria-controls="navbarCollapse"
                            onClick={ () => setMenuOpen( ( pOpen ) => !pOpen ) }
                            className={ "-ms-2 me-2 block rounded-lg px-2 ring-focus py-[6px] focus:ring-2 sm:px-3" + wide( "lg:hidden" )
                                + ( menuOpen ? " navbarTogglerActive" : "" ) }>
                            { /* bgzblah is defined nowhere in the app -- it is a leftover that
                                 never generated a rule -- so these three bars had a colour only
                                 under dark:. Below lg, where the toggler is the only way into the
                                 menu, light mode drew three transparent bars on bg-slate-100 and
                                 the hamburger was invisible. The repair was a slate-800 bar with
                                 a white one beside it under the dark variant; that pair is now the
                                 one token --color-chrome-ink, which is the same ink .navItem writes
                                 in and resolves to the same two values. A bar cannot lose half of
                                 it again, because there is no longer a half to lose. */ }
                            <span className="relative block bg-chrome-ink w-[30px] h-[2px] my-[6px]"></span>
                            <span className="relative block bg-chrome-ink w-[30px] h-[2px] my-[6px]"></span>
                            <span className="relative block bg-chrome-ink w-[30px] h-[2px] my-[6px]"></span>
                        </button>
                        <EsNavLogo />
                    </div>
                    { /* pe-7 from 42rem, not sm: at 640 to 671 a signed-in row (Donate, four discs, the
                         circle) with pe-7 ran 17px past a window with a scrollbar, and the page
                         scrolled sideways (Bell.feature), as the phone row did before 2026-10-22. */ }
                    <div className="flex w-full self-stretch items-center justify-between ps-2 pe-2 sm:ps-4 min-[42rem]:pe-7">
                        { /* lg:grow, so the two lists inside #navbarCollapse have
                             the row to spread across. Below lg this box is still
                             content-sized and the panel is still absolutely
                             positioned against the .relative row above, so
                             nothing about the phone layout turns on it. */ }
                        { /* Not .relative, so the panel inside anchors on the row and opens
                             from the bar's left edge, under the hamburger. */ }
                        <div className={ "flex self-stretch items-center" + wide( ( auth.authenticated ? "" : "lg:grow " ) + "lg:block lg:self-auto" ) }>

                            { /* bg-white had no dark counterpart below lg, where this is a
                                 floating panel rather than the bar itself. .navItem resolves to
                                 white ink in dark mode, so the dropdown was white text on a white
                                 panel -- the same bug as the hamburger above, in the other mode. The
                                 repair was a white panel with a blue-800 one beside it under the
                                 dark variant, one step up from the page's own blue-900, which is what
                                 a panel carrying shadow-lg should be; that is now --color-panel, the
                                 same token the back-to-top button in EsFooter reads.

                                 lg:bg-transparent is still here and its dark twin is gone. That twin
                                 existed only to out-rank the dark panel colour at desktop width, and
                                 with the mode moved inside the token there is nothing left for it to
                                 out-rank: bg-panel carries no variant at all, so a media-query
                                 utility beats it on its own. */ }
                            { /* lg:flex lg:justify-between, because BOTH lists live in
                                 here now -- see the note above es-nav-right below. At
                                 lg the panel is the row and the two lists are its ends;
                                 below lg it is the dropdown and they stack.

                                 The display class is written from `menuOpen` rather
                                 than toggled onto a static `hidden`. lg:flex still
                                 out-ranks either value of it, so the desktop bar does
                                 not care whether the phone menu was left open -- which
                                 is what the old class toggle relied on too.

                                 xl:pl-4, which was xl:px-4. The right half of that
                                 padding used to fall on nothing -- es-nav-right was
                                 outside this element -- and moving the list in here
                                 handed it 16px of inset it never had, measured as the
                                 whole group sliding 16px left of where it has always
                                 sat at xl and 2xl. The LEFT inset is real and is kept:
                                 it is 16px more air between the logo and the first entry. */ }
                            <nav id="navbarCollapse" data-testid="es-nav-menu" className={ ( menuOpen ? "block" : "hidden" ) + " absolute top-full inset-s-0 w-[240px] rounded-lg bg-panel py-5 shadow-lg sm:w-[250px]" + wide( "lg:static lg:flex lg:items-center lg:justify-between lg:w-full lg:max-w-full lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none xl:ps-4" ) }>
                                { /* <Link>, not <a onClick={navigate}>.
                                     Both halves of every nav entry are links -- item.view is
                                     an in-app route and item.href an external one -- but the
                                     first half used to be an anchor with no href and a click
                                     handler, which is a button wearing a link's clothes. It
                                     had no href to hover, no keyboard focus, and could not be
                                     middle-clicked or opened in a new tab; jsx-a11y flagged it
                                     three times over (anchor-is-valid, click-events-have-key-
                                     events, no-static-element-interactions) and was right on
                                     all three.
                                     <Link> renders a real <a href>, so the className and the
                                     aria-current carry over unchanged, .navItem still matches,
                                     and doNavInit's '#navbarCollapse ul li a' query -- the one
                                     that closes the mobile menu on a tap -- still finds it. */ }
                                <ul data-testid="es-nav-left" className={ "block" + wide( "lg:flex" ) }>
                                    { navList( leftNav ) }
                                </ul>

                                { /* ==== INSIDE the panel, 2026-09-15. THE PHONE BUG.
                                     This list -- Changelog, Devlog, Register, Login signed
                                     out; Admin, Dash, Account, Logout logged in -- sat in
                                     the box beside the dark toggle, carrying `hidden
                                     lg:flex`, and that box is NOT #navbarCollapse. Below
                                     lg it was therefore display:none with no way back:
                                     the hamburger toggles the panel, and Login and Register
                                     were not in the panel. On a phone the only way to
                                     log in was to type /view/login.

                                     lg was 960px then (EsCommon.css @theme), not
                                     Tailwind's stock 1024, which is why this was survivable
                                     on Android for so long -- a Pixel in landscape, and
                                     Chrome's Request Desktop Site, both land at or above
                                     960 and get the desktop bar. iOS Safari's Request
                                     Desktop Website changes the user agent and keeps
                                     honouring <meta name="viewport" content="width=
                                     device-width">, so an iPhone stays at ~390-430px and
                                     never reaches lg however the site is requested. Same
                                     markup, same bug, one platform that could not work
                                     around it.

                                     ONE copy of each entry, not a phone copy beside a
                                     desktop copy. es-nav-login-link, es-nav-register-link,
                                     es-nav-dash-link and es-nav-account-link are addressed
                                     by name from EsAppActions, EsAccountActions and
                                     EsShellActions; two elements answering one testid
                                     would hand Selenium whichever came first in tree
                                     order, which at desktop width would be a display:none
                                     one, and every AQA login would fail on an element it
                                     could not click.

                                     mx-8 is gone from the bare class and kept from lg up.
                                     It only ever applied where this list was hidden, and
                                     inside a 250px panel it would indent these four
                                     entries past the five above them. The lg/xl/2xl values
                                     are untouched -- they are part of the measured
                                     horizontal budget in EsNavBar.css. */ }
                                { /* Signed in, the right-hand entries (Admin, Account,
                                     Colorsheets, Logout) are the account circle's popup at the
                                     right end of the control row, and Subscribe leads that row,
                                     so there is no list here -- an empty one would draw the phone
                                     panel's rule over nothing. */ }
                                { auth.authenticated
                                    ? <ul data-testid="es-nav-right" className="hidden" />
                                    : <ul data-testid="es-nav-right" className={ "mt-2 border-t border-edge pt-2" + wide( "lg:mt-0 lg:flex lg:border-0 lg:pt-0 lg:mx-2 xl:mx-4 2xl:mx-6" ) }>
                                        { navList( rightNav ) }
                                    </ul> }
                            </nav>
                        </div>

                        { /* The live count, when variantEsCcuPlace is nav (EsCcu.js): its auto margins
                             centre it between the last entry and the row, which is why the box above
                             grows only signed out, when es-nav-right needs the room and the count is
                             never drawn. Folded, it rides against the row from lg, where the lists
                             gave the room; below lg the lists are already in the panel, so the count
                             is what gives, into the account menu (Bell.feature: at 640 a signed-in row
                             with it put the bell 33px past the window). */ }
                        <EsCcuCount where="nav" signedIn={ auth.authenticated } className={ "ms-auto me-1 text-chrome-ink max-sm:text-sm sm:me-3" + ( folded ? " max-lg:hidden" : " lg:me-auto" ) } />

                        <div className="flex items-center justify-end">
                            { /* Signed in, Subscribe or Donate leads the row; signed out on
                                 a phone, Login does (loginInRow). The row is outside the
                                 panel, so on a phone it stays on the bar. Then the
                                 theme menu: light/dark/system and the two colour grids. */ }
                            { auth.authenticated && subscribeItem && navLink( subscribeItem, ROW_ENTRY ) }
                            { loginInRow && navLink( crafterEntry( loginItem ), ROW_ENTRY ) }
                            <EsThemeMenu />
                            { /* The language menu, drawn only while rolloutEsTranslations is on. */ }
                            <EsLocaleMenu />
                            <EsFullscreenToggler />
                            { /* The bell (EsNotificationBell): every notice is a chat's, so it shows with the
                                 Commons. Its badge is laid over the disc, so the row's width never moves. */ }
                            { bellOnRow && <EsNotificationBell /> }
                            { auth.authenticated &&
                                <EsAccountCircle>
                                    { navList( rightNav ) }
                                    { /* The live count closes the menu when its place is menu, or
                                         below lg when the folded row cannot carry it. */ }
                                    <EsCcuCount where="menu" signedIn={ auth.authenticated } rowFolded={ folded }
                                                className={ ccuPlace === "nav" ? "lg:hidden" : "" } />
                                </EsAccountCircle> }
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}


